import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { DocumentListPanel } from './DocumentListPanel';
import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import { DocumentExtractionView } from './DocumentExtractionView';
import { useDeal } from '../contexts/DealContext';
import { DatabaseService } from '../lib/database';
import type { UserDocument } from '../types/database';
import { useScreenSize } from '../hooks/useScreenSize';
import { Menu, X } from 'lucide-react';

interface DocumentManagementSplitViewProps {
  className?: string;
  buyerId?: string;
  dealId?: string;
}

export function DocumentManagementSplitView({ 
  className = '', 
  buyerId,
  dealId
}: DocumentManagementSplitViewProps) {
  const { user } = useUser();
  const { selectedDeal } = useDeal();
  const { isMobile, isTablet } = useScreenSize();
  const [selectedDocument, setSelectedDocument] = useState<UserDocument | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showDocumentList, setShowDocumentList] = useState(true);
  const [showExtractionView, setShowExtractionView] = useState(false);
  const [extractingDocument, setExtractingDocument] = useState<UserDocument | null>(null);

  // Handle document selection
  const handleDocumentSelect = (document: UserDocument | null) => {
    setSelectedDocument(document);
    // On mobile and tablet, switch to preview when document is selected
    if ((isMobile || isTablet) && document) {
      setShowDocumentList(false);
    }
  };


  // Handle upload click - triggers bulk upload modal
  const handleUploadClick = () => {
    // Trigger the bulk upload modal in DocumentListPanel
    // We'll use a ref or state to communicate with DocumentListPanel
    setRefreshTrigger(prev => prev + 1); // This will cause DocumentListPanel to re-render and show modal
  };

  // Handle upload success
  const handleUploadSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
    setSelectedDocument(null); // Clear selection to show updated list
  };

  // Handle document updates (refresh list and selected document)
  const handleDocumentUpdate = async () => {
    setRefreshTrigger(prev => prev + 1);
    
    // If a document is selected, refresh its data
    if (selectedDocument) {
      try {
        const response = await DatabaseService.getUserDocuments(undefined, 1, 0, user?.id);
        if (response.data) {
          const updatedDocument = response.data.find(doc => doc.id === selectedDocument.id);
          if (updatedDocument) {
            setSelectedDocument(updatedDocument);
          }
        }
      } catch (error) {
        console.error('Failed to refresh selected document:', error);
      }
    }
  };

  // Handle extraction click
  const handleExtractionClick = (document: UserDocument) => {
    setExtractingDocument(document);
    setShowExtractionView(true);
  };

  // Handle extraction view close
  const handleExtractionViewClose = () => {
    setShowExtractionView(false);
    setExtractingDocument(null);
  };

  // Handle extraction complete
  const handleExtractionComplete = (extractionId: string) => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle document deletion
  const handleDocumentDelete = (deletedDocument: UserDocument) => {
    if (selectedDocument?.id === deletedDocument.id) {
      setSelectedDocument(null);
    }
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className={`flex h-full ${className}`}>
      {isMobile ? (
        // Mobile Layout - Stacked with toggle
        <div className="flex flex-col w-full">
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {showDocumentList ? 'Documents' : selectedDocument?.title || 'Document Preview'}
            </h2>
            <div className="flex items-center gap-2">
              {!showDocumentList && (
                <button
                  onClick={() => setShowDocumentList(true)}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                  title="Back to Documents"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              {showDocumentList && (
                <button
                  onClick={handleUploadClick}
                  className="p-2 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Upload Document"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Mobile Content */}
          <div className="flex-1 overflow-hidden">
            {showDocumentList ? (
              <DocumentListPanel
                buyerId={buyerId}
                dealId={dealId || selectedDeal?.deal_id}
                selectedDocument={selectedDocument}
                onDocumentSelect={handleDocumentSelect}
                onUploadClick={handleUploadClick}
                onDocumentUpdate={handleDocumentUpdate}
                onExtractionClick={handleExtractionClick}
                key={refreshTrigger}
                className="h-full"
              />
            ) : (
              <DocumentPreviewPanel
                selectedDocument={selectedDocument}
                onDocumentUpdate={handleDocumentUpdate}
                onDocumentDelete={handleDocumentDelete}
                onExtractionClick={handleExtractionClick}
                className="h-full"
              />
            )}
          </div>
        </div>
      ) : isTablet ? (
        // Tablet Layout - Stacked with toggle (similar to mobile but with tablet-specific styling)
        <div className="flex flex-col w-full">
          {/* Tablet Header */}
          <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {showDocumentList ? 'Documents' : selectedDocument?.title || 'Document Preview'}
            </h2>
            <div className="flex items-center gap-2">
              {!showDocumentList && (
                <button
                  onClick={() => setShowDocumentList(true)}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                  title="Back to Documents"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
              {showDocumentList && (
                <button
                  onClick={handleUploadClick}
                  className="p-2 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Upload Document"
                >
                  <Menu className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>

          {/* Tablet Content */}
          <div className="flex-1 overflow-hidden">
            {showDocumentList ? (
              <DocumentListPanel
                buyerId={buyerId}
                dealId={dealId || selectedDeal?.deal_id}
                selectedDocument={selectedDocument}
                onDocumentSelect={handleDocumentSelect}
                onUploadClick={handleUploadClick}
                onDocumentUpdate={handleDocumentUpdate}
                onExtractionClick={handleExtractionClick}
                key={refreshTrigger}
                className="h-full"
              />
            ) : (
              <DocumentPreviewPanel
                selectedDocument={selectedDocument}
                onDocumentUpdate={handleDocumentUpdate}
                onDocumentDelete={handleDocumentDelete}
                onExtractionClick={handleExtractionClick}
                className="h-full"
              />
            )}
          </div>
        </div>
      ) : (
        // Desktop Layout - Split View
        <>
          {/* Left Panel - Document List */}
          <div className="w-1/3 min-w-80 border-r border-gray-200">
            <DocumentListPanel
              buyerId={buyerId}
              dealId={dealId || selectedDeal?.deal_id}
              selectedDocument={selectedDocument}
              onDocumentSelect={handleDocumentSelect}
              onUploadClick={handleUploadClick}
              onDocumentUpdate={handleDocumentUpdate}
              key={refreshTrigger}
            />
          </div>

          {/* Right Panel - Document Preview */}
          <div className="flex-1">
            <DocumentPreviewPanel
              selectedDocument={selectedDocument}
              onDocumentUpdate={handleDocumentUpdate}
              onDocumentDelete={handleDocumentDelete}
              onExtractionClick={handleExtractionClick}
            />
          </div>
        </>
      )}


      {/* Extraction View Modal */}
      {showExtractionView && extractingDocument && (
        <DocumentExtractionView
          selectedDocument={extractingDocument}
          onClose={handleExtractionViewClose}
          onExtractionComplete={handleExtractionComplete}
        />
      )}
    </div>
  );
}
